'use client';

import { createWithEqualityFn } from 'zustand/traditional';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { Method2fa } from '@/types/enums/auth/Method2fa';
import { AuthStatus } from '@/types/enums/auth/AuthStatus';
import { authService, interactionService } from '@/services/api';
import { LoginStep } from '@/types/enums/auth/LoginStep';
import { config, isFeatureEnabled } from '@/config';
import { getErrorDetail, getFetchErrors } from '@/utils/getFetchErrorMessage';
import { accountService } from '@/services/api';
import { msToSeconds } from '@/utils/fp';
import { applyDevtools } from '../storeUtils';
import { usePhrasesStore, useAuthSessionStore, useConfigStore } from '..';
import { useAccountStore } from '@/store/accountStore';
import { getAccountLocale } from '@/utils/locale/getAccountLocale';
import type {
    AuthState,
    AuthActions,
    ILoginPropsOnLoginComplete,
    ILoginSuccessState,
} from './AuthStoreTypes';
import { setLocaleCookie } from '@/utils/locale/setLocaleCookie';
import { logToSentryOnFetchError } from '@/utils/fetch/logging/logToSentryOnFetchError';
import { FetchError } from '@/utils/fetch/FetchError/FetchError';
import { isArray } from '@/types/typeGuards/general';
import { IAccountSummary } from '@/types/types/forms/account/account.interface';
import { ICountryData } from '@/types/types/forms/dictionary/dictionary.interface';
import { browserRedirect } from '@/utils/fetch/redirect';

type StatusToLoginStep = {
    [key in AuthStatus]: LoginStep;
};

const statusToLoginStep: StatusToLoginStep = {
    [AuthStatus.Captcha]: LoginStep.Captcha,
    [AuthStatus['2fa']]: LoginStep.OtpMethod,
    [AuthStatus['2fa-submit']]: LoginStep.OtpVerify,
    [AuthStatus.Complete]: LoginStep.Login,
};

const initialState: AuthState = {
    step: LoginStep.Login,
    status: undefined,
    loggedIn: false,
    loggedInViaRegistration: false,
    isEmailConfirmed: undefined,
    subpanelName: null,
    subpanelId: null,
    loginTime: undefined,
    logoutTime: undefined,
    loggedInEmail: undefined,
    redirectJwt: {
        jwt: undefined,
        status: undefined,
    },
    passwordCheckTime: undefined,
    sessionStartTime: undefined,
    rememberMe: false,
    code2fa: {
        codeIds: {},
        method: undefined,
        timeSent: undefined,
        phoneNumber: undefined,
        secondsUntilNext2faCode: undefined,
        sentViaMethodPage: false,
    },
    isValid: {
        captcha: false,
        login: false,
        code2Fa: false,
    },
    interactionUid: undefined,
    checkingUidRedirect: false,
};

const pushRegistrationEventToDataLayer = (data: IAccountSummary) => {
    if (isArray(window?.dataLayer)) {
        window.dataLayer.push({
            event: 'registration',
            user_id: data.userId,
        });
    }
};

const isLoginComplete = (status: string | undefined): boolean => status === AuthStatus.Complete;

function updateLocaleBasedOnAccount(
    preferredLanguageId: number,
    countryCode: string,
    countriesDict: ICountryData[] | undefined,
    config: { defaultLocale: string; locales: string[] },
) {
    const hasAutoLocaleSwitchingDisabled = isFeatureEnabled('localeAutoSwitchingDisabled');
    if (hasAutoLocaleSwitchingDisabled) return;

    const accountLocale = getAccountLocale(preferredLanguageId, countryCode, countriesDict);
    const currentLocale = usePhrasesStore.getState().locale.locale;
    const { defaultLocale, locales } = config;
    const accountLocaleExists = locales.includes(accountLocale || '');
    const fallbackLocale = currentLocale || defaultLocale;
    const localeToSet = accountLocaleExists ? accountLocale : fallbackLocale;

    setLocaleCookie(localeToSet);
    usePhrasesStore.setState((state) => ({
        ...state,
        locale: {
            locale: localeToSet,
            hasBeenChecked: true,
        },
    }));
}

type LoginCompleteState = {
    loggedIn: AuthState['loggedIn'];
    loginTime?: AuthState['loginTime'];
    passwordCheckTime?: AuthState['passwordCheckTime'];
    sessionStartTime?: AuthState['sessionStartTime'];
    loggedInViaRegistration?: AuthState['loggedInViaRegistration'];
};

const onLoginComplete = async ({
    status,
    countriesDict,
    handleAccountSummaryFetched,
    isAccountCodeValidation = false,
}: ILoginPropsOnLoginComplete): Promise<{
    isEmailConfirmed?: boolean;
    hasUidRedirect?: boolean;
}> => {
    if (!isLoginComplete(status) || isAccountCodeValidation) {
        return { isEmailConfirmed: undefined };
    }
    useAuthSessionStore.setState({ isLoggedIn: true });

    const interactionUid = useAuthStore.getState().interactionUid;

    if (interactionUid) {
        useAuthStore.setState((state) => ({
            ...state,
            checkingUidRedirect: true,
        }));
        const res = await interactionService.checkLogin(interactionUid);
        if (res && res.redirectTo) {
            browserRedirect(res.redirectTo);
            return { hasUidRedirect: true };
        }
    }

    try {
        const accountSummary = await accountService.summary();
        const { isEmailConfirmed, subpanelName, subpanelId, preferredLanguageId, countryCode } =
            accountSummary;
        const { defaultLocale, locales } = config;
        useAuthStore.setState((state) => ({
            ...state,
            isEmailConfirmed,
            subpanelName,
            subpanelId,
            interactionUid: undefined,
        }));
        updateLocaleBasedOnAccount(preferredLanguageId, countryCode, countriesDict, {
            defaultLocale,
            locales,
        });
        handleAccountSummaryFetched?.(accountSummary);
        return { isEmailConfirmed, hasUidRedirect: false };
    } catch (err) {
        logToSentryOnFetchError(err as FetchError);
        return { isEmailConfirmed: undefined, hasUidRedirect: false };
    } finally {
        useAuthStore.setState((state) => ({
            ...state,
            checkingUidRedirect: false,
        }));
        // if jwt exists, status is set to redirected in useRedirects using useRedirectInfo,
        // otherwise set status to redirected after login (below)
        if (!useAuthStore.getState().redirectJwt.jwt) {
            useAuthStore.setState((state) => ({
                ...state,
                redirectJwt: {
                    jwt: undefined,
                    status: 'redirected',
                },
            }));
        }
    }
};

const getLoginProps = ({
    isLoggedIn = false,
    loggedInViaRegistration = false,
}): LoginCompleteState => ({
    loggedIn: isLoggedIn,
    loginTime: isLoggedIn ? new Date().toISOString() : undefined,
    passwordCheckTime: isLoggedIn ? new Date().toISOString() : undefined,
    sessionStartTime: isLoggedIn ? new Date().toISOString() : undefined,
    loggedInViaRegistration,
});

const loginSuccessState = ({
    state,
    status,
    newLoggedInEmail,
    newRememberMe,
    loggedInViaRegistration = false,
}: ILoginSuccessState): AuthState | Partial<AuthState> => {
    const isLoggedIn = isLoginComplete(status);
    const loginProps: LoginCompleteState = getLoginProps({ isLoggedIn, loggedInViaRegistration });

    return {
        ...state,
        ...loginProps,
        step: statusToLoginStep[status],
        status,
        isValid: {
            ...state.isValid,
            login: true,
        },
        loggedInEmail: newLoggedInEmail ?? state.loggedInEmail,
        rememberMe: newRememberMe ?? state.rememberMe,
    };
};

const loginFailedState = (state: AuthState): AuthState | Partial<AuthState> => {
    const loginProps: LoginCompleteState = getLoginProps({ isLoggedIn: false });
    return {
        ...state,
        ...loginProps,
        isValid: {
            ...state.isValid,
            login: false,
        },
    };
};

export const useAuthStore = createWithEqualityFn<AuthState & AuthActions>()(
    persist(
        applyDevtools(
            (set, get) => ({
                ...initialState,
                onLogin: async (loginDetails, countriesDict) => {
                    try {
                        const { status } = await authService.login(
                            loginDetails.email,
                            loginDetails.password,
                            loginDetails.rememberme,
                        );
                        set(
                            (state) =>
                                loginSuccessState({
                                    state,
                                    status,
                                    newLoggedInEmail: loginDetails.email,
                                    newRememberMe: loginDetails.rememberme,
                                }),
                            false,
                            'Authenticate user on login',
                        );
                        await onLoginComplete({ status, countriesDict });
                        return {
                            type: 'success',
                            message: status,
                        };
                    } catch (err) {
                        set((state) => loginFailedState(state), false, 'Error on authentication');
                        return {
                            type: 'error',
                            error: getFetchErrors(err),
                        };
                    }
                },
                loginOnRegComplete: async (registrationId, countriesDict) => {
                    try {
                        const status = await authService.loginOnRegComplete(registrationId);
                        set(
                            (state) =>
                                loginSuccessState({
                                    state,
                                    status,
                                    loggedInViaRegistration: true,
                                }),
                            false,
                            'Login on registration complete',
                        );
                        const { isEmailConfirmed } = await onLoginComplete({
                            status,
                            countriesDict,
                            handleAccountSummaryFetched: pushRegistrationEventToDataLayer,
                        });
                        return {
                            type: 'success',
                            message: status,
                            isEmailConfirmed,
                        };
                    } catch (err) {
                        set(
                            (state) => loginFailedState(state),
                            false,
                            'Error logging in on registration completion',
                        );
                        return {
                            type: 'error',
                            error: getFetchErrors(err),
                            isEmailConfirmed: undefined,
                        };
                    }
                },
                loginOnEnroll: async (enrollDetails, countriesDict) => {
                    try {
                        const { status } = await authService.enroll(enrollDetails);
                        set(
                            (state) =>
                                loginSuccessState({
                                    state,
                                    status,
                                    newLoggedInEmail: enrollDetails.email,
                                }),
                            false,
                            'Authenticate user on enroll',
                        );
                        await onLoginComplete({ status, countriesDict });
                        return {
                            type: 'success',
                            message: status,
                        };
                    } catch (err) {
                        set((state) => loginFailedState(state), false, 'Error on enroll');
                        return {
                            type: 'error',
                            error: getFetchErrors(err),
                        };
                    }
                },
                onPasswordCheck: async ({ currentPassword }) => {
                    try {
                        await accountService.sendPasswordCheck(currentPassword);
                        set(
                            { passwordCheckTime: new Date().toISOString() },
                            false,
                            'Check user password',
                        );
                        return {
                            type: 'success',
                            message: 'Password check successful',
                        };
                    } catch (err) {
                        set(
                            {
                                passwordCheckTime: undefined,
                            },
                            false,
                            'Error checking password',
                        );
                        return {
                            type: 'error',
                            error: getFetchErrors(err),
                        };
                    }
                },
                passwordCheckIsCurrent: () => {
                    const state = get();
                    const { website_sensitive_area_timeout } = useConfigStore.getState();
                    if (state.passwordCheckTime) {
                        const pct = new Date(state.passwordCheckTime);
                        if (
                            pct.valueOf() + msToSeconds(website_sensitive_area_timeout) >
                            Date.now()
                        ) {
                            return true;
                        }
                    }
                    return false;
                },
                sessionCheckIsCurrent: () => {
                    const state = get();
                    const { website_session_timeout, website_session_remember_me_timeout } =
                        useConfigStore.getState();
                    const sessionIsLoggedIn = useAuthSessionStore.getState().isLoggedIn;

                    if (!sessionIsLoggedIn && !state?.rememberMe) return false;

                    const sessionConfigTimeout = msToSeconds(
                        state?.rememberMe
                            ? website_session_remember_me_timeout
                            : website_session_timeout,
                    );

                    if (state.sessionStartTime) {
                        const pct = new Date(state.sessionStartTime);
                        if (pct.valueOf() + sessionConfigTimeout > Date.now()) {
                            return true;
                        }
                    }
                    return false;
                },
                checkCaptcha: async (captchaCode, countriesDict) => {
                    try {
                        const { status } = await authService.checkCaptcha(captchaCode);
                        set(
                            (state) => ({
                                ...loginSuccessState({
                                    state,
                                    status,
                                }),
                                isValid: {
                                    ...state.isValid,
                                    captcha: true,
                                },
                            }),
                            false,
                            'Authenticate user after captcha check',
                        );
                        const { hasUidRedirect } = await onLoginComplete({ status, countriesDict });

                        return {
                            type: 'success',
                            message: status,
                            hasUidRedirect,
                        };
                    } catch (err) {
                        set(
                            (state) => ({
                                ...state,
                                isValid: {
                                    ...state.isValid,
                                    captcha: false,
                                },
                            }),
                            false,
                            'Error on captcha check',
                        );
                        return {
                            type: 'error',
                            error: getFetchErrors(err),
                        };
                    }
                },
                sendMethod2fa: async (method, secondsUntilNext2faCode = 0) => {
                    try {
                        const isTOTP = method === Method2fa.Totp;
                        const res = !isTOTP
                            ? await authService.send2faMethod(method)
                            : { codeId: undefined, phoneNumber: undefined };
                        const { codeId, phoneNumber } = res;
                        set(
                            (state) => {
                                const { codeIds } = state.code2fa;
                                return {
                                    ...state,
                                    step: LoginStep.OtpVerify,
                                    code2fa: {
                                        ...state.code2fa,
                                        timeSent: new Date().toISOString(),
                                        secondsUntilNext2faCode: !isTOTP
                                            ? secondsUntilNext2faCode
                                            : undefined,
                                        phoneNumber: phoneNumber ?? state.code2fa.phoneNumber,
                                        codeIds: {
                                            ...codeIds,
                                            ...(method !== undefined && { [method]: codeId }),
                                        },
                                        method,
                                        sentViaMethodPage:
                                            window.location.pathname.includes('login/2fa/method'),
                                    },
                                };
                            },
                            false,
                            'Sending 2FA method',
                        );
                        return {
                            type: 'success',
                            message: codeId ?? 'noCodeId',
                        };
                    } catch (err) {
                        const getSecondsUntilNext2faCode = () => {
                            if (method === Method2fa.Email && getErrorDetail(err, 'secondsLeft')) {
                                return Number(getErrorDetail(err, 'secondsLeft'));
                            }
                            if (method === Method2fa.Sms) {
                                return secondsUntilNext2faCode; // no seconds left error for SMS so safer to make the user wait the default time
                            } else {
                                return secondsUntilNext2faCode;
                            }
                        };
                        set((state) => ({
                            ...state,
                            code2fa: {
                                ...state.code2fa,
                                secondsUntilNext2faCode: getSecondsUntilNext2faCode(),
                                method,
                                sentViaMethodPage: false,
                            },
                        }));
                        return {
                            type: 'error',
                            error: getFetchErrors(err),
                        };
                    }
                },
                resetCode2faSentViaMethodPage: () => {
                    set(
                        (state) => ({
                            ...state,
                            code2fa: {
                                ...state.code2fa,
                                sentViaMethodPage: false,
                            },
                        }),
                        false,
                        'Resetting code2faSentViaMethodPage',
                    );
                },
                validateCode2fa: async ({
                    method,
                    code,
                    codeId,
                    countriesDict,
                    isAccountCodeValidation,
                }) => {
                    try {
                        const { status } = await authService.validate2faCode(method, code, codeId);
                        set(
                            (state) => ({
                                ...loginSuccessState({
                                    state,
                                    status,
                                }),
                                isValid: {
                                    ...state.isValid,
                                    code2fa: true,
                                },
                            }),
                            false,
                            'Validating of 2FA code',
                        );
                        const { hasUidRedirect } = await onLoginComplete({
                            status,
                            countriesDict,
                            isAccountCodeValidation,
                        });
                        return {
                            type: 'success',
                            message: status,
                            hasUidRedirect,
                        };
                    } catch (err) {
                        if (!isAccountCodeValidation) {
                            const secondsUntilNext2faCode = getErrorDetail(err, 'secondsLeft')
                                ? Number(getErrorDetail(err, 'secondsLeft'))
                                : undefined;
                            set(
                                (state) => ({
                                    ...state,
                                    code2fa: {
                                        ...state.code2fa,
                                        secondsUntilNext2faCode,
                                    },
                                    isValid: {
                                        ...state.isValid,
                                        code2fa: false,
                                    },
                                }),
                                false,
                                'Error on validating of 2fa code',
                            );
                        }
                        return {
                            type: 'error',
                            error: getFetchErrors(err),
                        };
                    }
                },
                requestPasswordReset: async (email, captchaResponse) => {
                    try {
                        const message = await authService.requestPasswordReset(
                            email,
                            captchaResponse,
                        );
                        set(
                            {
                                step: LoginStep.ForgottenPassNewPass,
                            },
                            false,
                            'Requesting password reset',
                        );
                        return {
                            type: 'success',
                            message,
                        };
                    } catch (err) {
                        return {
                            type: 'error',
                            error: getFetchErrors(err),
                        };
                    }
                },
                passwordReset: async (
                    newPassword: string,
                    confirmNewPassword: string,
                    token: string,
                    hardReset?: boolean,
                    countriesDict?: ICountryData[],
                ) => {
                    try {
                        if (hardReset) {
                            const status = await authService.passwordHardReset(
                                newPassword,
                                confirmNewPassword,
                                token,
                            );
                            set(
                                (state) => ({
                                    ...loginSuccessState({
                                        state,
                                        status,
                                    }),
                                }),
                                false,
                                'Authenticate user after creating of new password',
                            );
                            await onLoginComplete({ status, countriesDict });
                            return {
                                type: 'success',
                                message: 'Logged in after password hard reset',
                            };
                        } else {
                            const message = await authService.passwordReset(
                                newPassword,
                                confirmNewPassword,
                                token,
                            );
                            set(
                                {
                                    step: LoginStep.ForgottenPassSuccessful,
                                },
                                false,
                                'Resetting of password',
                            );
                            return {
                                type: 'success',
                                message,
                            };
                        }
                    } catch (err) {
                        return {
                            type: 'error',
                            error: getFetchErrors(err),
                        };
                    }
                },
                logout: async () => {
                    try {
                        await authService.logout();
                        set(
                            () => ({
                                ...initialState,
                                logoutTime: new Date().toISOString(),
                            }),
                            false,
                            'Logging out',
                        );
                        useAccountStore.getState().reset();
                        useAuthSessionStore.setState({ isLoggedIn: false });
                        return {
                            type: 'success',
                            message: 'Succesfully logged out',
                        };
                    } catch (err) {
                        set(initialState, false, 'Logging out');
                        return {
                            type: 'error',
                            error: getFetchErrors(err),
                        };
                    }
                },
                setRedirectJwt: ({
                    jwt,
                    status,
                }: {
                    jwt: string | undefined;
                    status: 'checked' | 'redirected' | undefined;
                }) => {
                    set({ redirectJwt: { jwt, status } });
                },
                setInteractionUid: (uid: string) => {
                    set({ interactionUid: uid });
                },
            }),
            { name: `MPW ${config.theme} Auth Store` },
        ),
        {
            name: `mpw-${config.theme}-auth-storage`,
            partialize: (state) => ({
                step: state.step,
                loggedIn: state.loggedIn,
                loggedInEmail: state.loggedInEmail,
                isEmailConfirmed: state.isEmailConfirmed,
                subpanelName: state.subpanelName,
                subpanelId: state.subpanelId,
                loginTime: state.loginTime,
                logoutTime: state.logoutTime,
                passwordCheckTime: state.passwordCheckTime,
                sessionStartTime: state.sessionStartTime,
                redirectJwt: state.redirectJwt,
                status: state.status,
                code2fa: state.code2fa,
                rememberMe: state.rememberMe,
            }),
        },
    ),
    shallow,
);
